import { useEffect, useState } from 'react';

/**
 * Represents the possible states of the loading process.
 */
export enum LoadingState {
  /** Content is currently loading */
  loading = 'loading',
  /** An error occurred during loading */
  error = 'error',
  /** Loading completed successfully */
  success = 'success',
}

/**
 * Configuration options for the loading controller.
 */
type LoadingControllerOptions = {
  /**
   * Minimum duration in seconds that the loading state should be shown.
   * @default 3
   */
  minimumSeconds?: number;

  /**
   * External loading state that can be controlled from outside the hook.
   * @default LoadingState.success
   */
  externalLoadingState?: LoadingState;

  /**
   * Value between 0-100 where progress should halt if external loading isn't complete.
   * @default 90
   */
  progressHaltAt?: number;
};

/**
 * Return type for the loading controller hook.
 */
type LoadingControllerReturn = {
  /** Current loading state that respects minimum time requirements */
  loadingState: LoadingState;

  /** Current progress value (0-100) */
  progress: number;

  /** Whether the content is currently loading */
  isLoading: boolean;

  /** Whether an error occurred during loading */
  isError: boolean;

  /** Whether loading completed successfully */
  isSuccess: boolean;
};

/**
 * A hook that provides a controlled loading experience with a progress bar.
 *
 * This hook creates a loading experience that:
 * - Always shows for at least the specified minimum time
 * - Provides a smooth progress animation
 * - Can be synchronized with external loading states (like API requests)
 * - Can be configured to halt progress at a certain point until external loading completes
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { progress, isLoading } = useLoadingController();
 *
 * // With external loading state
 * const [apiState, setApiState] = useState(LoadingState.loading);
 * const { progress, isLoading } = useLoadingController({
 *   externalLoadingState: apiState,
 *   minimumSeconds: 2,
 *   progressHaltAt: 80
 * });
 * ```
 *
 * @param options - Configuration options for the loading controller
 * @returns Loading state and progress information
 */
export default function useLoadingController(options: LoadingControllerOptions = {}): LoadingControllerReturn {
  const {
    minimumSeconds = 3,
    externalLoadingState = LoadingState.success,
    progressHaltAt = 90, // Default to 90% if not specified
  } = options;

  const [internalLoadingState, setInternalLoadingState] = useState(LoadingState.loading);
  const [progress, setProgress] = useState(0);
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(false);
  const [progressHalted, setProgressHalted] = useState(false);

  // Update internal state when external state changes
  useEffect(() => {
    if (externalLoadingState !== internalLoadingState) {
      // Only update if we've met the minimum time requirement or if it's an error state
      if (minimumTimeElapsed || externalLoadingState === LoadingState.error) {
        setInternalLoadingState(externalLoadingState);
      }
    }

    // If external state is no longer loading, we can continue progress past the halt point
    if (externalLoadingState !== LoadingState.loading && progressHalted) {
      setProgressHalted(false);
    }
  }, [externalLoadingState, internalLoadingState, minimumTimeElapsed, progressHalted]);

  // Handle the progress bar and minimum time
  useEffect(() => {
    if (!minimumSeconds) {
      setMinimumTimeElapsed(true);
      return;
    }

    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        // Calculate the increment needed to reach 100% in the specified time
        // 100 / (minimumSeconds * 10) gives us the percentage to increment every 100ms
        const increment = 100 / (minimumSeconds * 10);

        // If we've reached the halt point and external state is still loading, halt progress
        if (prevProgress >= progressHaltAt && externalLoadingState === LoadingState.loading) {
          setProgressHalted(true);
          return prevProgress;
        }

        // Cap progress at 100
        if (prevProgress + increment >= 100) {
          clearInterval(interval);
          setMinimumTimeElapsed(true);
          return 100;
        }

        return prevProgress + increment;
      });
    }, 100);

    // Ensure we set minimumTimeElapsed after the specified time
    const timer = setTimeout(() => {
      setMinimumTimeElapsed(true);
      // If the external state is success or error but we were waiting for minimum time,
      // now we can update the internal state
      if (externalLoadingState !== LoadingState.loading) {
        setInternalLoadingState(externalLoadingState);
      }
    }, minimumSeconds * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [minimumSeconds, externalLoadingState, progressHaltAt]);

  return {
    loadingState: internalLoadingState,
    progress,
    isLoading: internalLoadingState === LoadingState.loading,
    isError: internalLoadingState === LoadingState.error,
    isSuccess: internalLoadingState === LoadingState.success,
  };
}
